import { useEffect, useState } from "react";
import { Trash2, Plus, AlertCircle } from "lucide-react";
import { deleteNotification, getNotification, sendNotification, uploadFile } from "../service/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { TablePagination } from "../components/ui/pagination";
import { RefreshCw } from "lucide-react";

const Notification = () => {
    const [addModal, setAddModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [formValues, setFormValues] = useState({ title: '', description: '' });
    const [image, setImage] = useState<File[] | null>(null);
    const [notificationImage, setNotificationImage] = useState([]);
    const [limit] = useState(3);
    const [offset, setOffset] = useState(1);
    const [pagination, setPagination] = useState<any>({});
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleteNotificationId, setDeleteNotificationId] = useState<any>();

    useEffect(() => {
        getAllNotification(offset);
    }, [offset]);

    const getAllNotification = async (offset: any) => {
        setLoading(true);
        try {
            let res = await getNotification(limit, offset);
            setNotifications(res.data.data);
            setPagination(res.data.pagination);
        } finally {
            setLoading(false);
        }
    }

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formValues.title.trim()) newErrors.title = "Title is required";
        if (!formValues.description.trim()) newErrors.description = "Description is required";
        if (!image || image.length === 0) newErrors.image = "At least one image is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e: any) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        if (!validateForm()) return;
        try {
            let body = {
                title: formValues.title,
                description: formValues.description,
                image: notificationImage[0],
                user_id: '-',
                user_type: "All"
            };
            await sendNotification(body);
            toast.success("Notification sent successfully");
            getAllNotification(offset);
            setAddModal(false);
            setFormValues({ title: '', description: '' });
            setImage(null);
        } catch (err) {
            toast.error("Failed to send notification");
        }
    }

    const handleFileChange = async (e: any) => {
        const files = e.target.files;
        if (!files.length) return;

        setImage(Array.from(files));
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            formData.append('file', file, file.name);
        }
        try {
            const response = await uploadFile(formData);
            setNotificationImage(response.data.files);
            setErrors({ ...errors, image: "" });
        } catch (error) {

            toast.error("Image upload failed");
        }
    };

    const handleDelete = (data: any) => {
        setDeleteModal(true);
        setDeleteNotificationId(data.id);
    };

    const deleteNotificationById = async () => {
        try {
            await deleteNotification({ id: deleteNotificationId });
            toast.success("Notification deleted");
            setDeleteModal(false);
            getAllNotification(offset);
        } catch (err) {
            toast.error("Failed to delete notification");
        }
    }

    return (
        <div className="w-full max-w-6xl mx-auto pb-8 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-foreground">Notifications</h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Push-style announcements to users. Compose a message and attach an image.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => getAllNotification(offset)}>
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                    <Button size="sm" onClick={() => setAddModal(true)}>
                        <Plus className="h-4 w-4 mr-1" /> Send notification
                    </Button>
                </div>
            </div>

            {/* Table card */}
            <Card className="overflow-hidden">
                {loading ? (
                    <div className="flex min-h-[200px] items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-12">#</TableHead>
                                    <TableHead>Title</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>User</TableHead>
                                    <TableHead>Image</TableHead>
                                    <TableHead className="text-right w-24">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {notifications.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center text-muted-foreground py-14">
                                            No notifications yet.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    notifications.map((item: any, index: number) => (
                                        <TableRow key={item.id}>
                                            <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                                            <TableCell className="font-medium">{item.title}</TableCell>
                                            <TableCell className="text-sm max-w-xs truncate">{item.description}</TableCell>
                                            <TableCell className="text-sm">{item.user_name}</TableCell>
                                            <TableCell>
                                                <img
                                                    src={item.image}
                                                    alt=""
                                                    className="h-14 w-24 object-cover rounded-lg border"
                                                    onError={(e) => { e.currentTarget.src = "/wood-blog-placeholder.jpg"; }}
                                                />
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                                    onClick={() => handleDelete(item)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
                {!loading && pagination?.last_page > 1 && (
                    <div className="border-t bg-muted/20 px-4 py-3">
                        <TablePagination
                            currentPage={offset}
                            totalPages={pagination?.last_page ?? 1}
                            onPageChange={(page) => setOffset(page)}
                        />
                    </div>
                )}
            </Card>

            {/* Send notification dialog */}
            <Dialog open={addModal} onOpenChange={setAddModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Send notification</DialogTitle>
                        <DialogDescription>Compose a push notification to send to all users.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="notif-title">Title</Label>
                            <Input
                                id="notif-title"
                                type="text"
                                name="title"
                                placeholder="Enter title"
                                value={formValues.title}
                                onChange={handleInputChange}
                            />
                            {errors.title && <p className="text-destructive text-xs">{errors.title}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="notif-desc">Description</Label>
                            <Textarea
                                id="notif-desc"
                                name="description"
                                placeholder="Enter description"
                                className="min-h-24"
                                value={formValues.description}
                                onChange={handleInputChange}
                            />
                            {errors.description && <p className="text-destructive text-xs">{errors.description}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <Label>Image</Label>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                            />
                            {errors.image && <p className="text-destructive text-xs">{errors.image}</p>}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddModal(false)}>Cancel</Button>
                        <Button onClick={handleSubmit}>Send</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader className="items-center text-center">
                        <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-2" />
                        <DialogTitle>Delete this notification?</DialogTitle>
                        <DialogDescription>This cannot be undone.</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex-row justify-center gap-2 sm:justify-center">
                        <Button variant="outline" onClick={() => setDeleteModal(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={deleteNotificationById}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default Notification;
